import type { Collection } from "@/lib/types";

const img = (seed: string, w = 1200, h = 900) =>
  ({
    url: `https://picsum.photos/seed/tilefort-col-${seed}/${w}/${h}`,
    altText: null,
    width: w,
    height: h,
  }) as const;

export const collections: Collection[] = [
  {
    id: "col-porcelain",
    handle: "porcelain",
    title: "Porcelain",
    description:
      "Low-maintenance porcelain tiles for kitchens, bathrooms and hallways — hard-wearing, water-resistant, timeless.",
    image: img("porcelain", 1600, 1100),
    productHandles: [
      "vienna-warm-oak-porcelain",
      "alba-ivory-porcelain",
      "marmo-statuario-porcelain",
      "luna-bone-porcelain",
      "noir-graphite-porcelain",
    ],
  },
  {
    id: "col-marble",
    handle: "marble",
    title: "Marble",
    description:
      "Natural and marble-effect tiles with soft veining. Add quiet luxury to bathrooms, feature walls and floors.",
    image: img("marble"),
    productHandles: [
      "carrara-classic-marble",
      "calacatta-gold-marble",
      "emperador-brown-marble",
      "travertine-silver-marble",
      "onyx-misty-marble",
    ],
  },
  {
    id: "col-metro",
    handle: "metro",
    title: "Metro",
    description:
      "Classic brick-bond metro tiles. A shop-front staple reworked in modern glazes, bevels and flats.",
    image: img("metro"),
    productHandles: [
      "classic-white-metro",
      "sage-flat-metro",
      "terracotta-hand-metro",
      "charcoal-bevel-metro",
      "powder-blue-metro",
    ],
  },
  {
    id: "col-mosaic",
    handle: "mosaic",
    title: "Mosaic",
    description:
      "Penny rounds, hex, fish-scale and chevron — textured mosaics on mesh-backed sheets for fast installation.",
    image: img("mosaic"),
    productHandles: [
      "penny-round-bone-mosaic",
      "hex-slate-mosaic",
      "fish-scale-teal-mosaic",
      "chevron-rose-mosaic",
      "square-ink-mosaic",
    ],
  },
  {
    id: "col-outdoor",
    handle: "outdoor",
    title: "Outdoor",
    description:
      "20mm porcelain pavers rated for frost, slip and stain. Pair with matching indoor tiles for seamless thresholds.",
    image: img("outdoor"),
    productHandles: [
      "terrace-sandstone-outdoor",
      "garden-slate-outdoor",
      "patio-oak-outdoor",
      "court-concrete-outdoor",
      "path-limestone-outdoor",
    ],
  },
  {
    id: "col-terracotta",
    handle: "terracotta",
    title: "Terracotta",
    description:
      "Rustic handmade-effect terracotta and clay tiles. Warm tones for kitchens, utility rooms and sunlit living spaces.",
    image: img("terracotta"),
    productHandles: [
      "tuscan-red-terracotta",
      "sienna-square-terracotta",
      "antique-rose-terracotta",
      "provence-sand-terracotta",
      "umbria-ember-terracotta",
    ],
  },
  {
    id: "col-large-format",
    handle: "large-format",
    title: "Large format",
    description:
      "Oversized slabs up to 1200×2600mm. Fewer grout lines, calmer rooms, showroom-grade finishes.",
    image: img("large-format"),
    productHandles: [
      "monolith-cloud-large-format",
      "quartzite-iron-large-format",
      "blanco-nieve-large-format",
      "slate-shadow-large-format",
      "concrete-pier-large-format",
    ],
  },
  {
    id: "col-wood-effect",
    handle: "wood-effect",
    title: "Wood effect",
    description:
      "The warmth of oak, the durability of porcelain. Realistic plank-format tiles for wet zones and underfloor heating.",
    image: img("wood-effect"),
    productHandles: [
      "oak-honey-plank",
      "smoked-walnut-plank",
      "nordic-ash-plank",
      "charred-pine-plank",
      "driftwood-pale-plank",
    ],
  },
];

export function findCollection(handle: string): Collection | null {
  return collections.find((c) => c.handle === handle) ?? null;
}
